import { useMemo, useState, type FormEvent } from "react";
import { Crosshair, ExternalLink, MapPin, Search } from "lucide-react";
import { assetUrl } from "@/lib/assets";

type SearchResult = {
  label: string;
  query: string;
};

export default function FindAChurchSection() {
  const [searchValue, setSearchValue] = useState("");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [message, setMessage] = useState("");
  const [locating, setLocating] = useState(false);

  const mapUrl = useMemo(() => {
    if (!result) return "";
    return `https://www.google.com/maps?q=${encodeURIComponent(result.query)}&output=embed`;
  }, [result]);

  const googleMapsUrl = useMemo(() => {
    if (!result) return "";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(result.query)}`;
  }, [result]);

  const runSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const location = searchValue.trim();

    if (!location) {
      setMessage("Escriba una provincia, cantón, distrito o comunidad para buscar.");
      return;
    }

    setMessage("");
    setResult({
      label: location,
      query: `iglesias cristianas en ${location}, Costa Rica`,
    });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMessage("Este navegador no permite obtener su ubicación. Puede escribir su comunidad manualmente.");
      return;
    }

    setLocating(true);
    setMessage("Solicitando permiso para usar su ubicación…");

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const coordinates = `${coords.latitude.toFixed(6)},${coords.longitude.toFixed(6)}`;
        setResult({
          label: "su ubicación actual",
          query: `iglesias cristianas cerca de ${coordinates}, Costa Rica`,
        });
        setMessage("");
        setLocating(false);
      },
      () => {
        setMessage("No pudimos obtener su ubicación. Puede escribir su comunidad manualmente.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  return (
    <section className="acad-church-finder" id="buscar-iglesia">
      <style>{`
        .acad-church-finder {
          padding: 20px 16px 46px;
          background: #f5f4f2;
          color: #fff;
        }
        .acad-church-finder-card {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          max-width: 1400px;
          min-height: 540px;
          margin: 0 auto;
          overflow: hidden;
          border-radius: 28px;
          background: linear-gradient(110deg, #008bb9 0%, #1b6aaa 54%, #34499a 100%);
          box-shadow: 0 18px 48px rgba(23, 55, 88, 0.17);
        }
        .acad-church-finder-copy {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: clamp(32px, 5vw, 72px);
        }
        .acad-church-finder h2 {
          margin: 0;
          color: #fff;
          font-size: clamp(2.5rem, 5vw, 4.5rem);
          font-weight: 520;
          letter-spacing: -0.055em;
          line-height: 0.96;
          text-transform: uppercase;
        }
        .acad-church-finder h2 span {
          display: inline-block;
          margin-top: 6px;
          padding: 4px 17px 7px;
          border-radius: 999px;
          background: #fff;
          color: #353533;
          line-height: 0.95;
        }
        .acad-church-finder-intro {
          max-width: 590px;
          margin-top: 25px;
          color: rgba(255, 255, 255, 0.88);
          font-size: 1rem;
          font-weight: 350;
          line-height: 1.58;
        }
        .acad-church-finder-form {
          max-width: 620px;
          margin-top: 26px;
        }
        .acad-church-finder-field {
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 5px 5px 5px 17px;
          border: 1px solid rgba(255, 255, 255, 0.54);
          border-radius: 12px;
          background: #fff;
          box-shadow: 0 7px 24px rgba(16, 43, 79, 0.15);
        }
        .acad-church-finder-field > svg {
          width: 20px;
          height: 20px;
          flex: 0 0 auto;
          color: #166aa2;
        }
        .acad-church-finder-field input {
          width: 100%;
          min-width: 0;
          padding: 12px 0;
          outline: 0;
          background: transparent;
          color: #252b31;
          font-size: 0.95rem;
        }
        .acad-church-finder-field input::placeholder { color: #737c84; }
        .acad-church-finder-submit {
          display: inline-flex;
          height: 47px;
          flex: 0 0 auto;
          align-items: center;
          gap: 8px;
          padding: 0 18px;
          border-radius: 9px;
          background: #353533;
          color: #fff;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          transition: background 180ms ease, transform 180ms ease;
        }
        .acad-church-finder-submit:hover {
          transform: translateY(-1px);
          background: #005c92;
        }
        .acad-church-finder-submit svg { width: 17px; height: 17px; }
        .acad-church-finder-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px 20px;
          align-items: center;
          margin-top: 15px;
        }
        .acad-church-location-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0;
          color: #fff;
          font-size: 0.76rem;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 4px;
        }
        .acad-church-location-button:disabled { cursor: wait; opacity: 0.65; }
        .acad-church-location-button svg { width: 17px; height: 17px; }
        .acad-church-finder-status {
          color: rgba(255, 255, 255, 0.82);
          font-size: 0.78rem;
          line-height: 1.4;
        }
        .acad-church-finder-note {
          margin-top: 22px;
          color: rgba(255, 255, 255, 0.65);
          font-size: 0.72rem;
          line-height: 1.45;
        }
        .acad-church-finder-visual {
          position: relative;
          min-height: 540px;
          background: #dbe5eb;
        }
        .acad-church-finder-photo,
        .acad-church-finder-map {
          width: 100%;
          height: 100%;
          min-height: inherit;
          border: 0;
          object-fit: cover;
        }
        .acad-church-finder-photo { display: block; }
        .acad-church-finder-photo-shade {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, rgba(34, 91, 142, 0.18), transparent 45%);
          pointer-events: none;
        }
        .acad-church-map-label {
          position: absolute;
          z-index: 2;
          right: 16px;
          bottom: 16px;
          left: 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 14px 16px;
          border-radius: 13px;
          background: rgba(33, 33, 31, 0.92);
          color: #fff;
          box-shadow: 0 8px 26px rgba(0, 0, 0, 0.24);
          backdrop-filter: blur(10px);
        }
        .acad-church-map-label div {
          display: flex;
          min-width: 0;
          align-items: center;
          gap: 9px;
        }
        .acad-church-map-label div svg {
          width: 18px;
          height: 18px;
          flex: 0 0 auto;
          color: #65c8ff;
        }
        .acad-church-map-label span {
          overflow: hidden;
          font-size: 0.75rem;
          font-weight: 700;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .acad-church-map-label a {
          display: inline-flex;
          flex: 0 0 auto;
          align-items: center;
          gap: 6px;
          color: #fff;
          font-size: 0.67rem;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .acad-church-map-label a:hover { color: #65c8ff; }
        .acad-church-map-label a svg { width: 15px; height: 15px; }
        @media (max-width: 850px) {
          .acad-church-finder { padding-inline: 12px; }
          .acad-church-finder-card { grid-template-columns: 1fr; }
          .acad-church-finder-visual { min-height: 430px; }
        }
        @media (max-width: 560px) {
          .acad-church-finder-field {
            display: grid;
            grid-template-columns: auto 1fr;
            padding: 7px 8px 8px 16px;
          }
          .acad-church-finder-submit {
            grid-column: 1 / -1;
            justify-content: center;
          }
          .acad-church-map-label {
            align-items: flex-start;
            flex-direction: column;
          }
        }
      `}</style>

      <div className="acad-church-finder-card">
        <div className="acad-church-finder-copy">
          <h2>
            Encuentre una <span>iglesia</span><br />cerca de usted
          </h2>
          <p className="acad-church-finder-intro">
            Escriba una provincia, cantón, distrito o comunidad de Costa Rica para ver
            iglesias cristianas registradas en Google Maps.
          </p>
          <form className="acad-church-finder-form" onSubmit={runSearch}>
            <label className="sr-only" htmlFor="acad-church-location">
              Provincia, cantón, distrito o comunidad
            </label>
            <div className="acad-church-finder-field">
              <MapPin aria-hidden="true" />
              <input
                id="acad-church-location"
                type="search"
                placeholder="Ejemplo: Moravia, San José"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                autoComplete="address-level2"
              />
              <button className="acad-church-finder-submit" type="submit">
                Buscar <Search aria-hidden="true" />
              </button>
            </div>
          </form>
          <div className="acad-church-finder-actions">
            <button
              className="acad-church-location-button"
              type="button"
              onClick={useCurrentLocation}
              disabled={locating}
            >
              <Crosshair aria-hidden="true" />
              {locating ? "Buscando ubicación…" : "Usar mi ubicación actual"}
            </button>
            <p className="acad-church-finder-status" role="status" aria-live="polite">
              {message}
            </p>
          </div>
          <p className="acad-church-finder-note">
            Los resultados son proporcionados por Google Maps y se limitan a búsquedas dentro
            de Costa Rica. Verifique horarios y datos directamente con cada congregación.
          </p>
        </div>

        <div className="acad-church-finder-visual">
          {result ? (
            <>
              <iframe
                className="acad-church-finder-map"
                title={`Iglesias cristianas cerca de ${result.label}`}
                src={mapUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <div className="acad-church-map-label">
                <div>
                  <MapPin aria-hidden="true" />
                  <span>Resultados cerca de {result.label}</span>
                </div>
                <a href={googleMapsUrl} target="_blank" rel="noreferrer">
                  Abrir en Maps <ExternalLink aria-hidden="true" />
                </a>
              </div>
            </>
          ) : (
            <>
              <img
                className="acad-church-finder-photo"
                src={assetUrl("Woman_Worship.jpg")}
                alt="Mujer participando en una congregación cristiana"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-church-finder-photo-shade" />
            </>
          )}
        </div>
      </div>
    </section>
  );
}
